import { useMemo } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import api from 'api';
import type { AccountResponse } from 'api/resources/accounts/types';
import useAccount, { getAccountQueryKey } from 'hooks/useAccount';
import { useToast } from 'hooks/useToast';
import { trackPendoEvent } from 'lib/pendo';

const EXTEND_TRIAL_TOAST_ID = 'extend-trial';
const EXTEND_TRIAL_WINDOW_DAYS = 30;
const EXTEND_TRIAL_PENDO_EVENT = 'trial_extension_requested';

const useExtendTrial = () => {
  const { accountId, account } = useAccount();
  const toast = useToast();
  const queryClient = useQueryClient();
  const trialExpires = account?.account_restrictions?.trial_expires;

  const canExtend = useMemo(() => {
    if (!account?.created_at || !trialExpires) {
      return false;
    }

    const createdAt = new Date(account.created_at);
    const expiresAt = new Date(trialExpires);
    const diffInDays =
      (expiresAt.getTime() - createdAt.getTime()) / (1000 * 3600 * 24);
    return diffInDays < EXTEND_TRIAL_WINDOW_DAYS;
  }, [account?.created_at, trialExpires]);

  const { mutateAsync, isLoading } = useMutation(
    () => api.extendAccountTrial(accountId),
    {
      onSuccess: data => {
        trackPendoEvent(EXTEND_TRIAL_PENDO_EVENT, { accountId, trialExpires });
        queryClient.setQueryData<AccountResponse>(
          getAccountQueryKey(accountId),
          oldData =>
            oldData ? { ...oldData, account_restrictions: data } : oldData,
        );
        toast.success({
          title: 'Trial extended by 30 days',
          description:
            'Enjoy using the platform, and if you have any questions, please feel free to reach out to us.',
          customId: EXTEND_TRIAL_TOAST_ID,
        });
      },
      onError: () => {
        toast.error({
          title: 'Failed to extend trial',
          customId: EXTEND_TRIAL_TOAST_ID,
        });
      },
    },
  );

  const extendTrial = async (): Promise<boolean> => {
    try {
      await mutateAsync();
      return true;
    } catch {
      return false;
    }
  };

  return {
    canExtend: canExtend || isLoading, //Stay consistent in optimistic update
    extendTrial,
    isLoading,
  };
};

export default useExtendTrial;
